import { OVERLAY_ENDE, SIG_ENDE, THUMB_ENDE } from '../domain/abgeleitet';
import { kompakteKennung } from '../domain/dateiname';
import { benenneUm, listeOrdner, loescheOrdner } from './dateien';
import { stelleOrdnerSicher } from './ordner';
import { logFolder, type LogKind, type LogScope } from './storage';

/**
 * Das Archiv gelöschter Prüfungen.
 *
 * **Zweistufig, Vorgabe des Nutzers (2026-09-10).** Wer eine Prüfung vom iPad löscht, löscht
 * ihre Datenbankzeilen — die Aufnahmen aber sind Beweismaterial und verschwinden nicht sofort.
 * Sie wandern nach `old/<Löschdatum>/logs/<Bezug>/<Art>/…`, also in dieselbe Struktur eine
 * Etage tiefer, und werden erst nach einer Frist endgültig gelöscht (Vorgabe sechs Monate,
 * in den Einstellungen änderbar).
 *
 * ⚠️ **Das Löschdatum steht im Pfad, nicht in einer Liste daneben.** Eine Liste kann von den
 * Dateien abweichen — wenn eine Datei von Hand entfernt oder eine Übertragung abgebrochen
 * wird, zeigt sie ins Leere und niemand merkt es. Ein Ordnername kann das nicht.
 *
 * ⚠️ **Gemessen wird ab dem Löschen, nicht ab der Aufnahme.** Eine Prüfung, die acht Monate
 * auf dem Gerät lag und heute gelöscht wird, behält ihre volle Frist. Nähme man die Zeit aus
 * dem Dateinamen, wäre sie beim nächsten Start sofort weg — und das ist genau der Fall, in
 * dem jemand noch etwas sucht.
 */

export const ARCHIV_WURZEL = 'old';

/*
 * Die drei Anhängsel einer Aufnahme kommen aus `domain/abgeleitet.ts` — einem reinen Modul, das
 * `archive.ts` importieren kann, ohne sich `storage.ts` samt Dateiplugin und der Zweitschrift
 * in die Fotos-Mediathek einzuhandeln. Bis zum 2026-09-14 stand die Liste hier als eigene
 * Kopie; drei Kopien liefen auseinander, und genau daran hing der Fehler mit der Markierebene.
 */
const SIG = SIG_ENDE;
const OVERLAY = OVERLAY_ENDE;
const THUMB = THUMB_ENDE;

/**
 * Wo das Stationsmaterial liegt (`server/classic/medien.ts::MEDIEN_WURZEL`). Wiederholt statt
 * importiert: `archive.ts` soll nichts vom Classic-Gleis wissen müssen.
 */
const MEDIEN_WURZEL = 'media';

/** Alle Ablageorte, in denen Dateien einer Prüfung liegen können. */
const SCOPES: LogScope[] = ['station', 'station-comment', 'task-comment'];
const KINDS: LogKind[] = ['screenshot', 'photo', 'audio', 'video', 'handwriting'];

/** `yyyy-MM-dd` — der Tagesordner im Archiv. */
export function tagesOrdner(jetzt: Date): string {
  const p = (v: number) => String(v).padStart(2, '0');
  return jetzt.getFullYear() + '-' + p(jetzt.getMonth() + 1) + '-' + p(jetzt.getDate());
}

/**
 * Gehört diese Datei zu der Prüfung?
 *
 * Geprüft wird auf das **ganze** Feld, nicht auf ein Vorkommen der Kennung irgendwo im Namen:
 * `exam=im-ss26` darf nicht auch `exam=im-ss26-nach` treffen. Der Wert ist derselbe, den
 * `buildFileName` schreibt — dort werden `_` und `=` durch `-` ersetzt.
 *
 * ⚠️ **Beide Formen**, weil in derselben Ablage beide liegen: `_exam=` mit der kompakten
 * Kennung seit dem 2026-09-21, `_examID=` mit der ausgeschriebenen davor.
 */
export function gehoertZurPruefung(dateiname: string, examId: string): boolean {
  return (
    dateiname.includes('_exam=' + examIdKompaktImNamen(examId) + '_') ||
    dateiname.includes('_examID=' + examIdImNamen(examId) + '_')
  );
}

/**
 * Wie `buildFileName` den Wert schreibt.
 *
 * ⚠️ **Beide Schreibweisen zählen** (seit 2026-09-21): neue Dateien tragen die Kennung kompakt
 * unter `_exam=`, die vor diesem Tag abgelegten ausgeschrieben unter `_examID=`. Sie liegen
 * nebeneinander in derselben Ablage, und eine gelöschte Prüfung muss **alle** ihre Aufnahmen
 * mitnehmen — eine übersehene bliebe für immer liegen, ohne Frist und ohne Eintrag.
 */
export const examIdImNamen = (examId: string) => examId.replace(/[_=]/g, '-');

/** Dieselbe Kennung, wie sie in einem Namen der kurzen Form steht. */
export const examIdKompaktImNamen = (examId: string) =>
  kompakteKennung(examId).replace(/[_=]/g, '-');

async function dateienIn(ordner: string): Promise<string[]> {
  try {
    const { dateien } = await listeOrdner(ordner);
    return dateien;
  } catch {
    // Ordner gibt es nicht — der Normalfall für die meisten Kombinationen aus Bezug und Art.
    return [];
  }
}

const ordnerAnlegen = stelleOrdnerSicher;

/**
 * Verschiebt alle Dateien einer Prüfung ins Archiv und meldet, wie viele es waren.
 *
 * Die Begleitdateien `.sig` tragen denselben Namen mit `.sig` dahinter und werden deshalb von
 * derselben Suche erfasst — Datei und Beglaubigung bleiben beieinander.
 *
 * Ein Fehlschlag bei einer einzelnen Datei hält den Rest nicht auf: besser neunundneunzig
 * archivierte Dateien und eine gemeldete als ein Abbruch, nach dem die Hälfte am alten Ort
 * liegt und niemand weiss, welche.
 */
export async function archiviereExamDateien(
  examId: string,
  log: (text: string) => Promise<void>,
  jetzt = new Date(),
): Promise<{ verschoben: number; fehlgeschlagen: number }> {
  const tag = tagesOrdner(jetzt);
  let verschoben = 0;
  let fehlgeschlagen = 0;

  for (const scope of SCOPES) {
    for (const kind of KINDS) {
      const quelle = logFolder(scope, kind);
      const treffer = (await dateienIn(quelle)).filter((n) => gehoertZurPruefung(n, examId));
      if (treffer.length === 0) continue;

      const ziel = ARCHIV_WURZEL + '/' + tag + '/' + quelle;
      await ordnerAnlegen(ziel);

      for (const name of treffer) {
        try {
          await benenneUm(quelle + '/' + name, ziel + '/' + name);
          verschoben += 1;
        } catch (fehler) {
          fehlgeschlagen += 1;
          await log('Archiv: ' + quelle + '/' + name + ' nicht verschoben: ' + String(fehler));
        }
      }
    }
  }

  if (verschoben > 0 || fehlgeschlagen > 0) {
    await log(
      'Archiv: ' + verschoben + ' Datei(en) der Prüfung ' + examId + ' nach ' +
        ARCHIV_WURZEL + '/' + tag + ' verschoben' +
        (fehlgeschlagen > 0 ? ', ' + fehlgeschlagen + ' fehlgeschlagen' : '') + '.',
    );
  }
  return { verschoben, fehlgeschlagen };
}

/**
 * Das **Stationsmaterial** einer Prüfung ins Archiv schieben.
 *
 * Die Anhänge einer Station (`GET /rest/media/{id}`, s. `server/classic/medien.ts`) liegen
 * unter `media/<Prüfung>/…` und **nicht** unter `logs/` — sie sind Vorlage des Servers, keine
 * Aufnahme des Prüfers. `archiviereExamDateien` sucht nur in den `logs`-Ordnern und liess sie
 * deshalb liegen: die Prüfung war vom Gerät verschwunden, ihr Material blieb für immer.
 *
 * Ins Archiv statt in den Papierkorb, damit es dieselbe Frist bekommt wie alles andere
 * (`raeumeArchiv`). Neu holen liesse es sich zwar jederzeit vom Server — aber genau in der
 * Lage, in der jemand danach sucht, ist der Server oft nicht erreichbar.
 */
export async function archiviereStationsmaterial(
  examId: string,
  log: (text: string) => Promise<void>,
  jetzt = new Date(),
): Promise<{ verschoben: number; fehlgeschlagen: number }> {
  // Wörtlich wie `medien.ts::dateiTauglich`; hier wiederholt aus demselben Grund wie `SIG`.
  const ordner = MEDIEN_WURZEL + '/' + examId.replace(/[^A-Za-z0-9._-]+/g, '-');
  const namen = await dateienIn(ordner);
  if (namen.length === 0) return { verschoben: 0, fehlgeschlagen: 0 };

  const ziel = ARCHIV_WURZEL + '/' + tagesOrdner(jetzt) + '/' + ordner;
  await ordnerAnlegen(ziel);

  let verschoben = 0;
  let fehlgeschlagen = 0;
  for (const name of namen) {
    try {
      await benenneUm(ordner + '/' + name, ziel + '/' + name);
      verschoben += 1;
    } catch (fehler) {
      fehlgeschlagen += 1;
      await log('Archiv: ' + ordner + '/' + name + ' nicht verschoben: ' + String(fehler));
    }
  }

  await log(
    'Archiv: ' + String(verschoben) + ' Anhang/Anhänge der Prüfung ' + examId + ' nach ' + ziel +
      ' verschoben' + (fehlgeschlagen > 0 ? ', ' + String(fehlgeschlagen) + ' fehlgeschlagen' : '') +
      '.',
  );
  return { verschoben, fehlgeschlagen };
}

/**
 * **Eine einzelne Aufnahme** ins Archiv schieben — dieselbe Ablage wie beim Prüfungslöschen.
 *
 * Wozu: Wer im Bogen auf den Papierkorb einer Aufnahme tippt, löscht Beweismaterial. Bis zum
 * 2026-09-13 war das sofort und endgültig; jetzt wandert die Datei nach
 * `old/<Löschdatum>/logs/<Bezug>/<Art>/…` und die Frist räumt sie später weg — genau wie bei
 * einer gelöschten Prüfung (Entscheidung des Nutzers, 2026-09-13).
 *
 * ⚠️ **Die Beglaubigung und die Markierebene gehen mit.** Eine `.sig` ohne ihre Datei wäre
 * eine Bescheinigung über nichts; eine liegengebliebene Ebene zeigte auf ein Bild, das nicht
 * mehr da ist — und ein neues Foto mit zufällig gleichem Namen bekäme fremde Marken
 * übergestülpt.
 *
 * ⚠️ **Nur die Hauptdatei entscheidet über Erfolg.** Eine fehlende `.sig` oder Ebene ist der
 * Normalfall (Ton und Video haben keine Ebene, ältere Aufnahmen keine Beglaubigung) und darf
 * nicht wie ein gescheitertes Archivieren aussehen.
 */
export async function archiviereDatei(
  pfad: string,
  log: (text: string) => Promise<void>,
  jetzt = new Date(),
): Promise<boolean> {
  if (pfad === '') return true;

  const schrägstrich = pfad.lastIndexOf('/');
  const ordner = schrägstrich < 0 ? '' : pfad.slice(0, schrägstrich);
  const ziel = ARCHIV_WURZEL + '/' + tagesOrdner(jetzt) + '/' + ordner;
  await ordnerAnlegen(ziel);

  const schiebe = async (von: string): Promise<boolean> => {
    try {
      await benenneUm(von, ziel + '/' + von.slice(schrägstrich + 1));
      return true;
    } catch {
      return false;
    }
  };

  const gelungen = await schiebe(pfad);
  if (!gelungen) {
    await log('Archiv: ' + pfad + ' nicht verschoben.');
    return false;
  }
  await schiebe(pfad + SIG);
  // Nur Fotos haben eine Ebene; bei Ton und Video läuft der Versuch ins Leere und wird
  // geschluckt — billiger als eine Fallunterscheidung nach Art, die hier nichts weiss.
  const ebene = pfad + OVERLAY;
  if (await schiebe(ebene)) await schiebe(ebene + SIG);
  // Das Vorschaubild eines Videos. Bliebe es liegen, zeigte es auf eine Datei, die nicht mehr
  // da ist — und der Ordner füllte sich mit Bildern zu nichts.
  await schiebe(pfad + THUMB);
  await log('Archiv: ' + pfad + ' nach ' + ziel + ' verschoben.');
  return true;
}

/** Tage zwischen zwei Tagesangaben `yyyy-MM-dd`; negativ, wenn der Ordner in der Zukunft liegt. */
export function alterInTagen(ordnerName: string, jetzt: Date): number | null {
  const treffer = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ordnerName);
  if (!treffer) return null;
  const [, jahr, monat, tag] = treffer;
  const damals = Date.UTC(Number(jahr), Number(monat) - 1, Number(tag));
  const heute = Date.UTC(jetzt.getFullYear(), jetzt.getMonth(), jetzt.getDate());
  return Math.round((heute - damals) / 86400000);
}

/**
 * Räumt das Archiv: Tagesordner, die älter als die Frist sind, verschwinden mitsamt Inhalt.
 *
 * ⚠️ **Ein Ordner, dessen Name kein Datum ist, bleibt stehen.** Er stammt nicht von hier, und
 * etwas Fremdes ungefragt zu löschen ist der falsche Umgang mit einem Verzeichnis, in dem
 * Prüfungsunterlagen liegen. Er wird stattdessen ins Dateilog gemeldet.
 */
export async function raeumeArchiv(
  aufbewahrenTage: number,
  log: (text: string) => Promise<void>,
  jetzt = new Date(),
): Promise<{ geloeschteOrdner: number }> {
  let ordner: string[];
  try {
    ordner = (await listeOrdner(ARCHIV_WURZEL)).ordner;
  } catch {
    // Kein Archiv — nichts zu räumen.
    return { geloeschteOrdner: 0 };
  }

  let geloeschteOrdner = 0;
  for (const name of ordner) {
    const alter = alterInTagen(name, jetzt);
    if (alter === null) {
      await log('Archiv: „' + name + '" ist kein Tagesordner und bleibt liegen.');
      continue;
    }
    if (alter <= aufbewahrenTage) continue;

    try {
      await loescheOrdner(ARCHIV_WURZEL + '/' + name);
      geloeschteOrdner += 1;
      await log('Archiv: ' + name + ' war ' + alter + ' Tage alt und wurde gelöscht.');
    } catch (fehler) {
      await log('Archiv: ' + name + ' liess sich nicht löschen: ' + String(fehler));
    }
  }
  return { geloeschteOrdner };
}
